import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

interface Location {
  lat: number;
  lng: number;
  accuracy: number;
  address?: string;
  timestamp: number;
}

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  watch?: boolean;
  onLocationChange?: (location: Location) => void;
  onError?: (error: GeolocationError) => void;
}

interface GeolocationError {
  code: number;
  message: string;
}

interface UseGeolocationReturn {
  location: Location | null;
  error: GeolocationError | null;
  isLoading: boolean;
  isWatching: boolean;
  getLocation: () => Promise<Location | null>;
  startWatching: () => void;
  stopWatching: () => void;
  calculateDistance: (targetLat: number, targetLng: number) => number | null;
}

export function useGeolocation(options: UseGeolocationOptions = {}): UseGeolocationReturn {
  const {
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 60000,
    watch = false,
    onLocationChange,
    onError
  } = options;

  const [location, setLocation] = useState<Location | null>(null);
  const [error, setError] = useState<GeolocationError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isWatching, setIsWatching] = useState(false);

  const watchIdRef = useRef<number | null>(null);

  // 处理定位错误
  const handleError = useCallback((error: GeolocationPositionError): GeolocationError => {
    let message = '定位失败';
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = '请允许使用定位权限';
        break;
      case error.POSITION_UNAVAILABLE:
        message = '无法获取位置信息';
        break;
      case error.TIMEOUT:
        message = '定位超时，请重试';
        break;
    }

    const err = { code: error.code, message };
    setError(err);
    onError?.(err);
    return err;
  }, [onError]);

  // 处理定位成功
  const handleSuccess = useCallback((position: GeolocationPosition): Location => {
    const newLocation: Location = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp
    };

    setLocation(newLocation);
    setError(null);
    onLocationChange?.(newLocation);
    return newLocation;
  }, [onLocationChange]);

  // 获取单次定位
  const getLocation = useCallback(async (): Promise<Location | null> => {
    if (!navigator.geolocation) {
      const err = { code: -1, message: '您的设备不支持定位功能' };
      setError(err);
      onError?.(err);
      toast.error('您的设备不支持定位功能');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy,
          timeout,
          maximumAge
        });
      });

      return handleSuccess(position);
    } catch (err) {
      handleError(err as GeolocationPositionError);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [enableHighAccuracy, timeout, maximumAge, handleSuccess, handleError, onError]);

  // 开始持续监听位置
  const startWatching = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('您的设备不支持定位功能');
      return;
    }

    if (watchIdRef.current !== null) {
      return; // 已经在监听中
    }

    setIsWatching(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      {
        enableHighAccuracy,
        timeout,
        maximumAge
      }
    );
  }, [enableHighAccuracy, timeout, maximumAge, handleSuccess, handleError]);

  // 停止监听位置
  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setIsWatching(false);
    }
  }, []);

  // 计算与目标位置的距离（米）
  const calculateDistance = useCallback((targetLat: number, targetLng: number): number | null => {
    if (!location) return null;

    const R = 6371000; // 地球半径（米）
    const lat1 = (location.lat * Math.PI) / 180;
    const lat2 = (targetLat * Math.PI) / 180;
    const deltaLat = ((targetLat - location.lat) * Math.PI) / 180;
    const deltaLng = ((targetLng - location.lng) * Math.PI) / 180;

    const a = 
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return Math.round(R * c);
  }, [location]);

  // 自动开始监听（如果watch为true）
  useEffect(() => {
    if (watch) {
      startWatching();
    }
    
    return () => {
      stopWatching();
    };
  }, [watch, startWatching, stopWatching]);

  return {
    location,
    error,
    isLoading,
    isWatching,
    getLocation,
    startWatching,
    stopWatching,
    calculateDistance
  };
}

// 逆地理编码（将坐标转换为地址）
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    // 这里可以接入高德地图或腾讯地图的逆地理编码API
    // 示例使用高德地图API
    const key = import.meta.env.VITE_AMAP_KEY;
    if (!key) return null;

    const response = await fetch(
      `https://restapi.amap.com/v3/geocode/regeo?key=${key}&location=${lng},${lat}&extensions=base`
    );
    
    const data = await response.json();
    if (data.status === '1' && data.regeocode) {
      return data.regeocode.formatted_address;
    }
    
    return null;
  } catch (error) {
    console.error('Reverse geocode error:', error);
    return null;
  }
}
